function SectionHeading({ children }: { children: React.ReactNode }) {
  return (
    <h2
      className="text-[#2C2825] text-2xl md:text-3xl leading-snug mb-4"
      style={{ fontFamily: "'Playfair Display', serif", fontWeight: 400 }}
    >
      {children}
    </h2>
  );
}

function SectionText({ children }: { children: React.ReactNode }) {
  return (
    <p
      className="text-[#6B5E53] text-base leading-[1.9] mb-0"
      style={{ fontFamily: "'Inter', sans-serif", fontWeight: 300 }}
    >
      {children}
    </p>
  );
}

function EditorialImage({ src, caption }: { src: string; caption: string }) {
  return (
    <div className="my-14 md:my-20 -mx-4 md:-mx-8">
      <div className="w-full aspect-[16/9] md:aspect-[21/9] overflow-hidden rounded-2xl bg-[#EDE6DA]">
        <img
          src={src}
          alt={caption}
          className="w-full h-full object-cover object-top"
        />
      </div>
      <p
        className="text-center text-[#B5A898] text-[11px] tracking-[0.15em] mt-4 uppercase"
        style={{ fontFamily: "'Inter', sans-serif" }}
      >
        {caption}
      </p>
    </div>
  );
}

function BulletList({ items }: { items: string[] }) {
  return (
    <ul className="mt-5 space-y-3">
      {items.map((item, i) => (
        <li key={i} className="flex items-start gap-3">
          <span className="mt-2 w-1.5 h-1.5 rounded-full bg-[#C8BAA8] flex-shrink-0" />
          <span
            className="text-[#6B5E53] text-base leading-[1.9]"
            style={{ fontFamily: "'Inter', sans-serif", fontWeight: 300 }}
          >
            {item}
          </span>
        </li>
      ))}
    </ul>
  );
}

export default function ArticleContentFirstHalf() {
  return (
    <div className="max-w-2xl mx-auto px-4 md:px-0">
      {/* Intro paragraph */}
      <div className="pt-16 pb-12 border-b border-[#EDE6DA]">
        <p
          className="text-[#4A3F38] text-lg md:text-xl leading-[1.85]"
          style={{ fontFamily: "'Playfair Display', serif", fontWeight: 400, fontStyle: "italic" }}
        >
          A soft beige living room does something remarkable — it makes a home feel quieter. When you walk in, the visual noise drops away. Warm neutrals absorb the busyness of daily life, natural materials anchor the senses, and soft light wraps the space in a kind of unhurried stillness. It is one of the most accessible and enduring approaches to a calm, expensive-feeling interior.
        </p>
      </div>

      {/* Image 1 */}
      <EditorialImage
        src="https://public.readdy.ai/ai/img_res/9a33ff1aa9e20502827e7c4117ad7031.jpg"
        caption="Soft Beige Living Room — Warm, Layered, Effortless"
      />

      {/* Section 1 — Why it works */}
      <div className="mb-14">
        <p
          className="text-[#B5A898] text-[10px] tracking-[0.45em] uppercase mb-3"
          style={{ fontFamily: "'Inter', sans-serif" }}
        >
          Why It Works
        </p>
        <SectionHeading>Why soft beige feels calm and expensive</SectionHeading>
        <SectionText>
          Soft beige works by reducing visual contrast. Unlike bright whites or saturated tones, beige absorbs and diffuses — it transitions gently across surfaces, materials, and shadows. The warm undertones create a sense of psychological ease, almost like the feeling of sunlight on skin. When paired with natural textures and diffused light, a beige room removes the effort of looking and lets the mind simply rest.
        </SectionText>
        <div className="mt-6">
          <SectionText>
            The reason it reads as expensive is more specific: warm neutrals are forgiving. They make mismatched pieces feel cohesive, they age gracefully, and they respond beautifully to natural light at every hour of the day. A room that looks good in morning sun, afternoon shade, and evening lamplight is a room that feels considered — and that quality is what most people associate with a high-end interior.
          </SectionText>
        </div>
      </div>

      {/* Section 2 — Key design elements */}
      <div className="mb-14">
        <p
          className="text-[#B5A898] text-[10px] tracking-[0.45em] uppercase mb-3"
          style={{ fontFamily: "'Inter', sans-serif" }}
        >
          Key Design Elements
        </p>
        <SectionHeading>The elements that make it work</SectionHeading>
        <SectionText>
          A soft beige living room is built from a small set of well-chosen elements. Each one contributes to the overall feeling of warmth and calm — and none of them need to be expensive to be effective.
        </SectionText>
        <BulletList
          items={[
            "A generous sofa in cream, oatmeal, or warm linen — the room's anchor piece",
            "Warm white or off-white walls with a yellow or pink undertone, never cool grey",
            "A neutral rug in jute, wool, or low-pile cotton to connect the floor to the furniture",
            "Light oak or ash wood in a coffee table, side table, or shelf",
            "Sheer linen curtains that diffuse rather than block natural light",
            "One warm floor lamp positioned near the seating area",
            "Soft textured cushions and a throw in cream, ivory, or sand tones",
            "One or two simple ceramic or natural decor pieces — nothing more",
          ]}
        />
      </div>

      {/* Image 2 */}
      <EditorialImage
        src="https://readdy.ai/api/search-image?query=natural%20light%20living%20room%20sheer%20linen%20curtains%20morning%20sun%20streaming%20window%20warm%20beige%20interior%20soft%20shadows%20beautiful%20calm%20atmosphere%20cream%20ivory%20neutral%20editorial%20photography%20architecture&width=1400&height=600&seq=sb_img2&orientation=landscape"
        caption="Natural Light Through Linen — The Room's Best Feature"
      />

      {/* Section 3 — Natural light */}
      <div className="mb-14">
        <SectionHeading>Use natural light as part of the design</SectionHeading>
        <SectionText>
          In a beige interior, natural light does not simply illuminate — it animates. Morning sun touching linen turns the fabric golden. Afternoon light deepens the shadows in a woven rug. Dusk softens everything into a single warm tone. Lean into this by keeping windows uncluttered. Choose sheer linen curtains that diffuse rather than block light. Place seating near windows so that sunlight reaches the sofa, the rug, and the table. In a soft beige room, daylight is the most important design element you have — and it costs nothing.
        </SectionText>
      </div>

      {/* Section 4 — Texture */}
      <div className="mb-14">
        <SectionHeading>Layer texture to create quiet depth</SectionHeading>
        <SectionText>
          When your palette is deliberately restrained, texture becomes the visual language of the room. A linen cushion, a woven throw, a bouclé accent chair, a brushed cotton blanket — these create gentle contrast without colour. Add a wool or jute rug for floor texture, a matte ceramic vase for a surface accent, and a few knit or rattan elements for organic warmth. The goal is not visual excitement but visual richness — a room that rewards slow looking.
        </SectionText>
        <div className="mt-6">
          <SectionText>
            The key is keeping all textures in the same warm tonal family. Mixing cool grey linen with warm cream bouclé creates subtle visual tension that disrupts the calm. Stay within the warm spectrum — cream, ivory, sand, oatmeal, taupe — and the room will feel cohesive even with many different materials in play.
          </SectionText>
        </div>
      </div>

      {/* Image 3 */}
      <EditorialImage
        src="https://readdy.ai/api/search-image?query=warm%20neutral%20styling%20details%20close%20up%20living%20room%20linen%20cushions%20ceramic%20bowl%20dried%20stems%20wooden%20tray%20matte%20surfaces%20layered%20textures%20beige%20cream%20ivory%20tones%20editorial%20photography%20interior%20calm%20atmosphere%20beautiful&width=1400&height=600&seq=sb_img3&orientation=landscape"
        caption="Layered Textures in Warm Neutral Tones"
      />

      {/* Section 5 — Styling tips */}
      <div className="mb-14">
        <p
          className="text-[#B5A898] text-[10px] tracking-[0.45em] uppercase mb-3"
          style={{ fontFamily: "'Inter', sans-serif" }}
        >
          Styling Tips
        </p>
        <SectionHeading>Practical tips for getting it right</SectionHeading>
        <SectionText>
          The most common mistake in a beige living room is adding too much. The palette is so forgiving that it is easy to keep layering — another cushion, another plant, another decorative object — until the room loses its sense of calm. Here are the principles that keep it feeling right:
        </SectionText>
        <BulletList
          items={[
            "Keep all undertones warm and consistent — mixing cool greys with warm beiges creates subtle visual tension",
            "Leave empty space between furniture pieces; the space itself is part of the design",
            "Choose decor that feels chosen rather than collected — one ceramic vase beats five",
            "Use warm-toned bulbs (2700K–3000K) in lamps to maintain the room's warmth after dark",
            "Avoid high-gloss surfaces; matte and satin finishes absorb light more softly",
            "If you add a plant, choose one with soft, rounded leaves rather than sharp architectural forms",
            "Keep books spine-out or face-down on shelves to reduce visual noise",
          ]}
        />
      </div>

      {/* Section 6 — Furniture */}
      <div className="mb-14">
        <SectionHeading>Keep furniture simple and easy to live with</SectionHeading>
        <SectionText>
          A calm living room is rarely about furniture collecting. It works best with a comfortable sofa — the kind you actually fall into — one or two accent chairs, a simple wooden coffee table at a comfortable height, a warm floor lamp positioned near seating, a small side table for a book and a cup, and open empty space around and between each piece. The empty space is not empty: it is rest for the eye and room for the light to move through.
        </SectionText>
      </div>

      {/* Section 7 — Decor */}
      <div className="mb-14">
        <SectionHeading>Choose decor that supports the mood</SectionHeading>
        <SectionText>
          Decor in a beige living room should feel chosen rather than collected. A single ceramic vase, a small stack of books with spines turned out, a simple candle holder, a subtle stem or branch in a narrow vessel, a quietly framed piece of art — each object should be placed with intention and space around it. The urge to fill shelves and surfaces is worth resisting. In a calm room, each object carries more weight precisely because it is not competing with twenty others.
        </SectionText>
      </div>
    </div>
  );
}
